import React, { useCallback, useEffect, useMemo, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Tabs, Tab, Theme, Chip } from '@mui/material';
import { useGetTabRecordsQuery } from '@/store/tabsApi';
import { useGetAccountTagsQuery } from '@/store/accountsApi';

// Lazy-load tab components for code splitting
const AccountsTab = lazy(() => import('./tabs/AccountsTab'));
const CategoriesTab = lazy(() => import('./tabs/CategoriesTab'));
const AccountTypesTab = lazy(() => import('./tabs/AccountTypesTab'));
const InstitutionsTab = lazy(() => import('./tabs/InstitutionsTab'));
const TagsTab = lazy(() => import('./tabs/TagsTab'));
const GroupTab = lazy(() => import('./tabs/GroupTab'));

interface SavedFilterEditorProps {
  excludedAccounts: number[];
  excludedCategories: number[];
  excludedAccountTypes: number[];
  excludedInstitutions: number[];
  excludedTags: number[];
  excludedGroups: Record<string, string[]>;
  activeTab: string;
  setActiveTab: (next: string) => void;
  onChange: (update: {
    excludedAccounts: number[];
    excludedCategories: number[];
    excludedAccountTypes: number[];
    excludedInstitutions: number[];
    excludedTags: number[];
    excludedGroups: Record<string, string[]>;
  }) => void;
  /** When provided, each tab's "Clear all" button clears all exclusion filters across all tabs. */
  onClearAllFilters?: () => void;
  // Formik instance from parent to manage all filter state
  formik: {
    values: {
      excludedAccounts: number[];
      excludedCategories: number[];
      excludedAccountTypes: number[];
      excludedInstitutions: number[];
      excludedTags: number[];
      excludedGroups: Record<string, string[]>;
    };
    setFieldValue: (
      field: string,
      value: number[] | Record<string, string[]>,
    ) => void;
  };
  // Counts calculated from Formik values in parent (persists across tab changes)
  excludedAccountsCount: number;
  excludedCategoriesCount: number;
  excludedAccountTypesCount: number;
  excludedInstitutionsCount: number;
  excludedTagsCount: number;
  /** Per-tab count for group tabs: TabUIKey -> excluded count */
  excludedGroupsCountByKey: Record<string, number>;
  supportedFilters?: FilterCapability[];
  /** Scopes the account options to one client. See FilterSelectionModal. */
  investorId?: number;
}

export type FilterCapability =
  | 'accounts'
  | 'categories'
  | 'types'
  | 'institutions'
  | 'tags'
  | 'groups';

const GROUP_TAB_KEY_REGEX = /^(investor|advisor):\d+$/;

const capabilityForTabKey = (tabKey: string): FilterCapability | undefined => {
  if (GROUP_TAB_KEY_REGEX.test(tabKey)) return 'groups';
  switch (tabKey) {
    case 'accounts':
    case 'categories':
    case 'types':
    case 'institutions':
    case 'tags':
      return tabKey;
    default:
      return undefined;
  }
};

const SavedFilterEditor: React.FC<SavedFilterEditorProps> = React.memo(
  ({
    excludedAccounts: _excludedAccountsProp,
    excludedCategories: _excludedCategoriesProp,
    excludedAccountTypes: _excludedAccountTypesProp,
    excludedInstitutions: _excludedInstitutionsProp,
    excludedTags: _excludedTagsProp,
    excludedGroups: _excludedGroupsProp,
    activeTab,
    setActiveTab,
    onChange,
    onClearAllFilters,
    formik,
    excludedAccountsCount,
    excludedCategoriesCount,
    excludedAccountTypesCount,
    excludedInstitutionsCount,
    excludedTagsCount,
    excludedGroupsCountByKey,
    supportedFilters,
    investorId,
  }) => {
    const { t } = useTranslation();
    const { data: accountGroups } = useGetAccountTagsQuery();

    // Use Formik values directly from parent - no local state needed
    // All tabs are now self-contained and load their own data
    // excludedAccounts, excludedCategories, excludedAccountTypes, excludedInstitutions are not used in component body

    const { data: allTabRecords, isLoading: tabsLoading } =
      useGetTabRecordsQuery('investor-filters');

    const tabRecords = useMemo(() => {
      if (!allTabRecords || !supportedFilters) {
        return allTabRecords;
      }
      return allTabRecords.filter((tab) => {
        const capability = capabilityForTabKey(tab.TabUIKey);
        return (
          capability !== undefined && supportedFilters.includes(capability)
        );
      });
    }, [allTabRecords, supportedFilters]);

    // Compute a valid activeTab value - default to first tab if activeTab is empty or invalid
    const validActiveTab = useMemo(() => {
      if (!tabRecords || tabRecords.length === 0) {
        return activeTab || '';
      }

      // If activeTab is valid (exists in tabRecords), use it
      const isValidTab = tabRecords.some((tab) => tab.TabUIKey === activeTab);
      if (isValidTab) {
        return activeTab;
      }

      // Otherwise default to first tab
      return tabRecords[0].TabUIKey;
    }, [activeTab, tabRecords]);

    // Initialize activeTab in parent, and re-seed it when it stops being offered
    useEffect(() => {
      if (!tabRecords || tabRecords.length === 0) {
        return;
      }
      if (!tabRecords.some((tab) => tab.TabUIKey === activeTab)) {
        setActiveTab(tabRecords[0].TabUIKey);
      }
    }, [tabRecords, activeTab, setActiveTab]);

    const handleTabChange = useCallback(
      (_event: React.SyntheticEvent, newValue: string) => {
        if (newValue === 'all') {
          // Clear all filters using Formik
          formik.setFieldValue('excludedAccounts', []);
          formik.setFieldValue('excludedCategories', []);
          formik.setFieldValue('excludedAccountTypes', []);
          formik.setFieldValue('excludedInstitutions', []);
          formik.setFieldValue('excludedTags', []);
          formik.setFieldValue('excludedGroups', {});
          onChange({
            excludedAccounts: [],
            excludedCategories: [],
            excludedAccountTypes: [],
            excludedInstitutions: [],
            excludedTags: [],
            excludedGroups: {},
          });
          return;
        }

        setActiveTab(newValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
      },
      [formik, onChange],
    );

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        {tabRecords && tabRecords.length > 0 && (
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: 2,
              flexShrink: 0,
            }}
          >
            <Tabs
              value={validActiveTab}
              onChange={handleTabChange}
              sx={{ mb: 3 }}
              variant='scrollable'
              scrollButtons='auto'
              aria-label='scrollable filter tabs'
            >
              {tabRecords.map((tab) => (
                <Tab
                  key={tab.TabUIKey}
                  value={tab.TabUIKey}
                  sx={{ textTransform: 'none' }}
                  label={
                    <Box
                      component='span'
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      {tab.TabUIKey === 'types'
                        ? t('dataSource')
                        : tab.TabUIKey === 'categories'
                          ? t('accountType')
                          : tab.Label}
                      {tab.TabUIKey === 'accounts' &&
                        excludedAccountsCount > 0 && (
                          <Chip
                            label={t('hiddenCount', {
                              count: excludedAccountsCount,
                            })}
                            sx={(theme: Theme) => ({
                              backgroundColor: theme.palette.custom.shadow.dark,
                              color: theme.palette.text.secondary,
                              padding: theme.spacing(0.2),
                              lineHeight: '1rem',
                              height: 'auto',
                              fontSize: '0.75rem',
                            })}
                          />
                        )}
                      {tab.TabUIKey === 'categories' &&
                        excludedCategoriesCount > 0 && (
                          <Chip
                            label={t('hiddenCount', {
                              count: excludedCategoriesCount,
                            })}
                            sx={(theme: Theme) => ({
                              backgroundColor: theme.palette.custom.shadow.dark,
                              color: theme.palette.text.secondary,
                              padding: theme.spacing(0.2),
                              lineHeight: '1rem',
                              height: 'auto',
                              fontSize: '0.75rem',
                            })}
                          />
                        )}
                      {tab.TabUIKey === 'types' &&
                        excludedAccountTypesCount > 0 && (
                          <Chip
                            label={t('hiddenCount', {
                              count: excludedAccountTypesCount,
                            })}
                            sx={(theme: Theme) => ({
                              backgroundColor: theme.palette.custom.shadow.dark,
                              color: theme.palette.text.secondary,
                              padding: theme.spacing(0.2),
                              lineHeight: '1rem',
                              height: 'auto',
                              fontSize: '0.75rem',
                            })}
                          />
                        )}
                      {tab.TabUIKey === 'institutions' &&
                        excludedInstitutionsCount > 0 && (
                          <Chip
                            label={t('hiddenCount', {
                              count: excludedInstitutionsCount,
                            })}
                            sx={(theme: Theme) => ({
                              backgroundColor: theme.palette.custom.shadow.dark,
                              color: theme.palette.text.secondary,
                              padding: theme.spacing(0.2),
                              lineHeight: '1rem',
                              height: 'auto',
                              fontSize: '0.75rem',
                            })}
                          />
                        )}
                      {tab.TabUIKey === 'tags' && excludedTagsCount > 0 && (
                        <Chip
                          label={t('hiddenCount', { count: excludedTagsCount })}
                          sx={(theme: Theme) => ({
                            backgroundColor: theme.palette.custom.shadow.dark,
                            color: theme.palette.text.secondary,
                            padding: theme.spacing(0.2),
                            lineHeight: '1rem',
                            height: 'auto',
                            fontSize: '0.75rem',
                          })}
                        />
                      )}
                      {GROUP_TAB_KEY_REGEX.test(tab.TabUIKey) &&
                        (excludedGroupsCountByKey[tab.TabUIKey] ?? 0) > 0 && (
                          <Chip
                            label={t('hiddenCount', {
                              count:
                                excludedGroupsCountByKey[tab.TabUIKey] ?? 0,
                            })}
                            sx={(theme: Theme) => ({
                              backgroundColor: theme.palette.custom.shadow.dark,
                              color: theme.palette.text.secondary,
                              padding: theme.spacing(0.2),
                              lineHeight: '1rem',
                              height: 'auto',
                              fontSize: '0.75rem',
                            })}
                          />
                        )}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>
        )}

        {tabsLoading ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', py: 4, flex: 1 }}
          >
            <CircularProgress />
          </Box>
        ) : tabRecords && tabRecords.length > 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {/* Keep all tabs mounted but hidden to prevent re-mounting and content shift */}
            {/* Use a stable wrapper to prevent scroll position loss */}
            <Box
              sx={{
                display:
                  validActiveTab === 'accounts' || !validActiveTab
                    ? 'flex'
                    : 'none',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              }}
              // Use a stable key to prevent React from recreating the DOM element
              key='accounts-tab-wrapper'
            >
              <Suspense
                fallback={
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                }
              >
                <AccountsTab
                  key='accounts-tab' // Stable key to prevent unmounting
                  initialExcludedAccounts={formik.values.excludedAccounts}
                  initialExcludedCategories={formik.values.excludedCategories}
                  setFieldValue={formik.setFieldValue}
                  onClearAllFilters={onClearAllFilters}
                  investorId={investorId}
                />
              </Suspense>
            </Box>
            <Box
              sx={{
                display: validActiveTab === 'categories' ? 'flex' : 'none',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              }}
            >
              <Suspense
                fallback={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      py: 4,
                      flex: 1,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                }
              >
                <CategoriesTab
                  initialExcludedCategories={formik.values.excludedCategories}
                  setFieldValue={formik.setFieldValue}
                  onClearAllFilters={onClearAllFilters}
                />
              </Suspense>
            </Box>
            <Box
              sx={{
                display: validActiveTab === 'types' ? 'flex' : 'none',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              }}
            >
              <Suspense
                fallback={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      py: 4,
                      flex: 1,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                }
              >
                <AccountTypesTab
                  initialExcludedAccountTypes={
                    formik.values.excludedAccountTypes
                  }
                  setFieldValue={formik.setFieldValue}
                  onClearAllFilters={onClearAllFilters}
                />
              </Suspense>
            </Box>
            <Box
              sx={{
                display: validActiveTab === 'institutions' ? 'flex' : 'none',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              }}
            >
              <Suspense
                fallback={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      py: 4,
                      flex: 1,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                }
              >
                <InstitutionsTab
                  initialExcludedInstitutions={
                    formik.values.excludedInstitutions
                  }
                  setFieldValue={formik.setFieldValue}
                  onClearAllFilters={onClearAllFilters}
                />
              </Suspense>
            </Box>
            <Box
              sx={{
                display: validActiveTab === 'tags' ? 'flex' : 'none',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              }}
            >
              <Suspense
                fallback={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      py: 4,
                      flex: 1,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                }
              >
                <TagsTab
                  initialExcludedTags={formik.values.excludedTags}
                  setFieldValue={formik.setFieldValue}
                />
              </Suspense>
            </Box>
            {accountGroups &&
              tabRecords
                .filter((tab) => GROUP_TAB_KEY_REGEX.test(tab.TabUIKey))
                .map((tab) => {
                  const group = accountGroups.find(
                    (g) =>
                      `${g.IsAdvisorTag ? 'advisor' : 'investor'}:${g.TagCategoryId}` ===
                      tab.TabUIKey,
                  );
                  if (!group) return null;
                  return (
                    <Box
                      key={tab.TabUIKey}
                      sx={{
                        display:
                          validActiveTab === tab.TabUIKey ? 'flex' : 'none',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                      }}
                    >
                      <Suspense
                        fallback={
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              py: 4,
                              flex: 1,
                            }}
                          >
                            <CircularProgress />
                          </Box>
                        }
                      >
                        <GroupTab
                          groupId={tab.TabUIKey}
                          groupName={group.Name}
                          tags={group.Tags ?? []}
                          initialExcludedTagNames={
                            formik.values.excludedGroups[tab.TabUIKey] ?? []
                          }
                          setFieldValue={formik.setFieldValue}
                          getExcludedGroups={() => formik.values.excludedGroups}
                        />
                      </Suspense>
                    </Box>
                  );
                })}
          </Box>
        ) : null}
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison - re-render when counts change (for chip updates)
    // or when other important props change
    // formik.setFieldValue should be stable, but we compare it to be safe
    return (
      prevProps.activeTab === nextProps.activeTab &&
      prevProps.setActiveTab === nextProps.setActiveTab &&
      prevProps.onChange === nextProps.onChange &&
      prevProps.onClearAllFilters === nextProps.onClearAllFilters &&
      prevProps.formik.setFieldValue === nextProps.formik.setFieldValue &&
      // Re-render when counts change so chips update
      prevProps.excludedAccountsCount === nextProps.excludedAccountsCount &&
      prevProps.excludedCategoriesCount === nextProps.excludedCategoriesCount &&
      prevProps.excludedAccountTypesCount ===
        nextProps.excludedAccountTypesCount &&
      prevProps.excludedInstitutionsCount ===
        nextProps.excludedInstitutionsCount &&
      prevProps.excludedTagsCount === nextProps.excludedTagsCount &&
      prevProps.excludedGroupsCountByKey ===
        nextProps.excludedGroupsCountByKey &&
      prevProps.excludedAccounts === nextProps.excludedAccounts &&
      prevProps.excludedCategories === nextProps.excludedCategories &&
      prevProps.excludedAccountTypes === nextProps.excludedAccountTypes &&
      prevProps.excludedInstitutions === nextProps.excludedInstitutions &&
      prevProps.excludedTags === nextProps.excludedTags &&
      prevProps.excludedGroups === nextProps.excludedGroups &&
      prevProps.supportedFilters === nextProps.supportedFilters &&
      prevProps.investorId === nextProps.investorId
    );
  },
);

SavedFilterEditor.displayName = 'SavedFilterEditor';

export default SavedFilterEditor;
